import { useEffect, useState } from 'react';
import { IconPlus } from '@tabler/icons-react';
import { useParams, useSearchParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import ProjectBrandingChatPreview from './CreateProjectBrandingModal/ProjectBrandingChatPreview';
import ProjectBrandingConsentPreview from './CreateProjectBrandingModal/ProjectBrandingConsentPreview';
import ProjectBrandingInputs from './CreateProjectBrandingModal/ProjectBrandingInputs';
import { useMutateCreateProjectTheme, useMutateUpdateProjectTheme } from '@/reactQuery/post';
import { ECreateThemeModalType, EStatusCode } from '@/enums';
import axiosInstance from '@/axios/axiosInstance';
import useTranslation from '@/hooks/useTranslation';
import ToastMessage from '../toast-message/ToastMessage';
import { Theme } from '@/types';
import { useGetTheme } from '@/reactQuery/get';

interface IModalProps {
  openModal: boolean;
  setOpenModal: React.Dispatch<React.SetStateAction<boolean>>;
}

function CreateProjectBrandingModal({ openModal, setOpenModal }: IModalProps) {
  const { lang } = useTranslation();
  const { isUserAdmin, isUserOwner } = useOrganizationRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const [brandingName, setBrandingName] = useState('');
  const [handleLogo, setHandleLogo] = useState<string | null>(null);
  const [handleFavicon, setHandleFavicon] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [faviconFile, setFaviconFile] = useState<File | null>(null);
  const [originalLogoUrl, setOriginalLogoUrl] = useState<string | null>(null);
  const [originalFaviconUrl, setOriginalFaviconUrl] = useState<string | null>(null);
  const [primary1Color, setPrimary1Color] = useState('#096846');
  const [primary2Color, setPrimary2Color] = useState('#FF6F98');
  const [secondaryColor, setSecondaryColor] = useState('#FFE8ED');
  const [backgroundColor, setBackgroundColor] = useState('#F3F4F6');
  const [progressBarColor, setProgressBarColor] = useState('#FF6F98');
  const [consentBackgroundColor, setConsentBackgroundColor] = useState('#FF6F98');
  const { organization_id: organizationId } = useParams();
  const modalMode = searchParams.get('mode');
  const themeId = searchParams.get('id');
  const { data: currentThemeColors } = useGetTheme(organizationId, themeId);

  const themeIsDefault = currentThemeColors?.name === 'Default';

  const addQueryParams = (params: Record<string, string>) => {
    Object.entries(params).forEach(([key, value]) => {
      searchParams.set(key, value);
    });
    setSearchParams(searchParams);
  };

  const removeQueryParams = (keys: string[]) => {
    keys.forEach((key) => {
      searchParams.delete(key);
    });
    setSearchParams(searchParams);
  };

  const resetColorsToDefault = () => {
    setBrandingName('');
    setHandleLogo(null);
    setHandleFavicon(null);
    setLogoFile(null);
    setFaviconFile(null);
    setOriginalLogoUrl(null);
    setOriginalFaviconUrl(null);
    setPrimary1Color('#096846');
    setPrimary2Color('#FF6F98');
    setSecondaryColor('#FFE8ED');
    setBackgroundColor('#F3F4F6');
    setProgressBarColor('#FF6F98');
    setConsentBackgroundColor('#FF6F98');
  };

  const handleExitModal = () => {
    // Restore original state when canceling
    if (modalMode === ECreateThemeModalType.EDIT) {
      setHandleLogo(originalLogoUrl);
      setHandleFavicon(originalFaviconUrl);
      setLogoFile(null);
      setFaviconFile(null);
    }

    setOpenModal(false);
    removeQueryParams(['mode', 'id']);
  };

  const handleShowModal = () => {
    addQueryParams({ mode: 'create' });
    setOpenModal(true);
    resetColorsToDefault();
  };

  const { mutateAsync: mutateAsyncCreateTheme } = useMutateCreateProjectTheme(organizationId);
  const { mutateAsync: mutateAsyncUpdateTheme } = useMutateUpdateProjectTheme(organizationId);

  const onSubmit = async () => {
    try {
      if (themeIsDefault) {
        toast.error('Cannot modify the Default brand.', { containerId: 'modal-toast' });
        return;
      }

      if (!brandingName) {
        toast.error(
          <ToastMessage title="brandingNameMissing" description="addBrandingNameToFinish" />,
          { containerId: 'modal-toast' },
        );
        return;
      }

      const transformedData: Theme = {
        name: brandingName,
        primary: primary1Color,
        'chat-secondary': secondaryColor,
        'chat-primary': primary2Color,
        'chat-background': backgroundColor,
        'chat-consent-bg': consentBackgroundColor,
        'chat-progress-bar-bg': progressBarColor,
      };

      let createdThemeId = themeId;

      if (modalMode === ECreateThemeModalType.CREATE) {
        const response = await mutateAsyncCreateTheme(transformedData);
        createdThemeId = response.data._id;
        toast.success(lang.get('msg.themeSuccessfullyCreated'));
      }
      if (modalMode === ECreateThemeModalType.EDIT) {
        await mutateAsyncUpdateTheme({ formData: transformedData, themeId: themeId! });
        toast.success(lang.get('msg.themeSuccessfullyUpdated'));
      }

      // Handle logo removal - if we had a logo before but now it's null
      if (
        modalMode === ECreateThemeModalType.EDIT &&
        originalLogoUrl &&
        !handleLogo &&
        createdThemeId
      ) {
        try {
          await axiosInstance.delete(
            `/organizations/${organizationId}/themes/${createdThemeId}/logo`,
          );
        } catch (error) {
          console.error('Failed to remove logo:', error);
        }
      }

      // Handle favicon removal - if we had a favicon before but now it's null
      if (
        modalMode === ECreateThemeModalType.EDIT &&
        originalFaviconUrl &&
        !handleFavicon &&
        createdThemeId
      ) {
        try {
          await axiosInstance.delete(
            `/organizations/${organizationId}/themes/${createdThemeId}/favicon`,
          );
        } catch (error) {
          console.error('Failed to remove favicon:', error);
        }
      }

      // Upload logo if a file was selected
      if (logoFile && createdThemeId) {
        const logoFormData = new FormData();
        logoFormData.append('logo', logoFile);

        try {
          await axiosInstance.post(
            `/organizations/${organizationId}/themes/${createdThemeId}/logo`,
            logoFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );
        } catch (error) {
          console.error('Failed to upload logo:', error);
          toast.error('Theme created but logo upload failed', { containerId: 'modal-toast' });
        }
      }

      // Upload favicon if a file was selected
      if (faviconFile && createdThemeId) {
        const faviconFormData = new FormData();
        faviconFormData.append('favicon', faviconFile);

        try {
          await axiosInstance.post(
            `/organizations/${organizationId}/themes/${createdThemeId}/favicon`,
            faviconFormData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              },
            },
          );
        } catch (error) {
          console.error('Failed to upload favicon:', error);
          toast.error('Theme created but favicon upload failed', { containerId: 'modal-toast' });
        }
      }

      handleExitModal();
    } catch (err) {
      const axiosError = err as AxiosError;

      if (axiosError?.status === 409) {
        toast.error(lang.get('msg.themeWithSameNameExists'), { containerId: 'modal-toast' });
        return;
      }

      if (axiosError?.status === EStatusCode.INVALID_ROLE) {
        toast.error(lang.get('msg.invalidRole'), { containerId: 'modal-toast' });
        return;
      }

      toast.error(lang.get('msg.errorPleaseTryAgain'), { containerId: 'modal-toast' });
    }
  };

  useEffect(() => {
    if (modalMode === ECreateThemeModalType.EDIT && themeId) {
      // Reset to defaults first while waiting for data
      if (!currentThemeColors) {
        resetColorsToDefault();
      } else {
        setBrandingName(currentThemeColors.name || '');
        setPrimary1Color(currentThemeColors.primary || '#096846');
        setPrimary2Color(currentThemeColors['chat-primary'] || '#FF6F98');
        setSecondaryColor(currentThemeColors['chat-secondary'] || '#FFE8ED');
        setBackgroundColor(currentThemeColors['chat-background'] || '#F3F4F6');
        setProgressBarColor(currentThemeColors['chat-progress-bar-bg'] || '#FF6F98');
        setConsentBackgroundColor(currentThemeColors['chat-consent-bg'] || '#FF6F98');

        // Set logo and favicon URLs if they exist and store original values
        if (currentThemeColors.logo_url) {
          setHandleLogo(currentThemeColors.logo_url);
          setOriginalLogoUrl(currentThemeColors.logo_url);
        } else {
          setHandleLogo(null);
          setOriginalLogoUrl(null);
        }
        if (currentThemeColors.favicon_url) {
          setHandleFavicon(currentThemeColors.favicon_url);
          setOriginalFaviconUrl(currentThemeColors.favicon_url);
        } else {
          setHandleFavicon(null);
          setOriginalFaviconUrl(null);
        }
      }
    } else if (modalMode === ECreateThemeModalType.CREATE) {
      resetColorsToDefault();
    }
  }, [currentThemeColors, themeId, modalMode]);

  return (
    <AlertDialog open={openModal}>
      <AlertDialogTrigger asChild onClick={handleShowModal}>
        <Button
          disabled={!isUserAdmin && !isUserOwner}
          variant="default"
          className="flex items-center gap-1"
        >
          <IconPlus size={18} />
          {lang.get('msg.addNewBranding')}
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="w-full flex flex-col max-w-[95%] h-[95%]">
        <AlertDialogHeader className="w-full h-full overflow-y-auto">
          <AlertDialogTitle>
            {themeIsDefault
              ? `${lang.get('msg.viewingDefaultBranding')} (${lang.get('msg.readOnly')})`
              : lang.get('msg.designYourBrandsColorPalette')}
          </AlertDialogTitle>
          <div className="flex w-full h-full gap-6 p-1.5">
            {/* editing */}
            <div className="flex flex-col w-[30%]">
              <h2 className="mb-2 text-lg font-semibold">{lang.get('msg.settings')}:</h2>
              <ProjectBrandingInputs
                brandingName={brandingName}
                setBrandingName={setBrandingName}
                primary1Color={primary1Color}
                primary2Color={primary2Color}
                backgroundColor={backgroundColor}
                consentBackgroundColor={consentBackgroundColor}
                progressBarColor={progressBarColor}
                secondaryColor={secondaryColor}
                setBackgroundColor={setBackgroundColor}
                setConsentBackgroundColor={setConsentBackgroundColor}
                setOpenModal={setOpenModal}
                setPrimary1Color={setPrimary1Color}
                setPrimary2Color={setPrimary2Color}
                setProgressBarColor={setProgressBarColor}
                setSecondaryColor={setSecondaryColor}
                handleExitModal={handleExitModal}
                onSubmit={onSubmit}
                themeIsDefault={themeIsDefault}
                handleLogo={handleLogo}
                setHandleLogo={setHandleLogo}
                handleFavicon={handleFavicon}
                setHandleFavicon={setHandleFavicon}
                logoFile={logoFile}
                setLogoFile={setLogoFile}
                faviconFile={faviconFile}
                setFaviconFile={setFaviconFile}
              />
            </div>

            {/* previews */}
            <div className="flex flex-col w-[70%]">
              <h2 className="mb-2 text-lg font-semibold">{lang.get('msg.preview')}:</h2>
              <div
                style={{ backgroundColor }}
                className="flex items-center gap-8 h-full justify-center overflow-hidden w-full min-w-[800px] p-5 rounded-lg shadow-sm"
              >
                {/* consent screen */}
                <ProjectBrandingConsentPreview
                  consentBackgroundColor={consentBackgroundColor}
                  handleLogo={handleLogo}
                  primary1Color={primary1Color}
                />

                {/* chat screen */}
                <ProjectBrandingChatPreview
                  primary1Color={primary1Color}
                  primary2Color={primary2Color}
                  progressBarColor={progressBarColor}
                  secondaryColor={secondaryColor}
                />
              </div>
            </div>
          </div>
        </AlertDialogHeader>
        {/* Toast container for modal-specific toasts */}
        <ToastContainer
          position="top-center"
          autoClose={5000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          style={{
            zIndex: 999999999999,
            width: 'auto',
            minWidth: '340px',
            maxWidth: '520px',
            fontSize: '0.875rem',
          }}
          containerId="modal-toast"
        />
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default CreateProjectBrandingModal;
